import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Home } from 'lucide-react';
import { SEO } from '../components/SEO';
import { LandingNav } from '../components/Landing/LandingNav';
import Footer from '../components/Layout/Footer';

export default function NotFound() {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth?mode=signup');
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  // Rotating hero images for visual interest
  const heroImages = [
    '/images/styles-page/modern_staging_style.webp',
    '/images/styles-page/luxury_staging_style.webp',
    '/images/styles-page/coastal_staging_style.webp',
    '/images/styles-page/scandinavian_staging_style.webp',
  ];
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Gallery images for the showcase
  const galleryImages = [
    { src: '/images/styles-page/modern_bedroom.webp', alt: 'Modern bedroom staging' },
    { src: '/images/styles-page/luxury_bedroom.webp', alt: 'Luxury bedroom staging' },
    { src: '/images/styles-page/coastal_living_room.webp', alt: 'Coastal living room staging' },
    { src: '/images/styles-page/scandinavian_living_room.webp', alt: 'Scandinavian living room staging' },
  ];

  const quickLinks = [
    { to: '/virtual-staging/styles', label: 'Explore Styles', description: 'See all 21 design styles' },
    { to: '/virtual-staging/pricing', label: 'View Pricing', description: 'Simple, transparent plans' },
    { to: '/virtual-staging/before-after', label: 'See Transformations', description: 'Real before & after results' },
    { to: '/auth?mode=signup', label: 'Get Started', description: '3 free credits today' },
  ];

  return (
    <div className="bg-alabaster text-charcoal min-h-screen">
      <SEO
        title="404 - Page Not Found | VirtualStagePro"
        description="This page seems to be empty. Let's stage it with something beautiful instead."
        canonical={typeof window !== 'undefined' ? window.location.href : undefined}
      />

      {/* Floating Navigation */}
      <LandingNav onGetStarted={handleGetStarted} />

      {/* Hero Section - Full Screen with 404 */}
      <section 
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          {heroImages.map((src, index) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: prefersReducedMotion 
                    ? 'none' 
                    : `scale(${1 + (scrollY / (typeof window !== 'undefined' ? window.innerHeight : 800)) * 0.1})`,
                }}
              >
                <img
                  src={src}
                  alt="Virtual staging"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Warm gradient overlay - Atelier style */}
              <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 via-charcoal/40 to-olive/50" />
            </div>
          ))}
        </div>

        {/* Soft terracotta glow accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120vw] h-[30vh] bg-gradient-to-t from-terracotta/15 to-transparent blur-3xl pointer-events-none" />
        
        <div className="relative z-10 text-center max-w-6xl mx-auto px-6 pt-20 md:pt-0">
          {/* Playful 404 */}
          <div className="mb-8 relative">
            <div className="text-9xl md:text-[12rem] lg:text-[16rem] font-serif font-light tracking-tight leading-none text-alabaster/20">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-9xl md:text-[12rem] lg:text-[16rem] font-serif font-light tracking-tight leading-none text-olive/30 blur-sm">
                404
              </div>
            </div>
          </div>

          <div className="mb-8 inline-flex items-center px-4 py-2 rounded-full bg-alabaster/20 backdrop-blur-sm border border-alabaster/30">
            <span className="text-xs font-mono uppercase tracking-widest text-alabaster/90">
              Page Not Found
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-light tracking-tight mb-6 leading-none text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.3)]">
            This page is empty.
            <br />
            <span className="text-alabaster/80">Let's stage it instead.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-alabaster/90 font-sans font-light max-w-3xl mx-auto mb-12 leading-relaxed">
            Looks like this page needs some virtual staging. While we work on that, explore our beautiful design styles and transform your properties.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link
              to="/virtual-staging/styles"
              className="inline-flex items-center gap-2 px-8 py-4 bg-olive hover:bg-charcoal text-alabaster transition-all duration-500 text-lg font-medium rounded-full shadow-lift hover:-translate-y-0.5"
            >
              Explore Styles
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center px-8 py-4 border border-alabaster/40 hover:border-alabaster/70 text-white hover:text-white transition-all duration-500 text-lg font-light rounded-full backdrop-blur-sm"
            >
              Get Started Free
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <div className="w-px h-16 bg-alabaster/30">
            <div className="w-px h-8 bg-alabaster/60" />
          </div>
        </div>
      </section>

      {/* Gallery Section - Showcase Beautiful Staging */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              While You're Here
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              Explore beautiful
              <br />
              <span className="text-charcoal/60">staged spaces</span>
            </h2>
            <p className="text-xl text-sand font-light max-w-2xl mx-auto leading-relaxed">
              Every empty room is an opportunity. See how we transform properties with AI-powered virtual staging.
            </p>
          </div>

          {/* Image Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {galleryImages.map((img, index) => (
              <Link
                key={img.src}
                to="/virtual-staging/styles"
                className="group relative aspect-[3/4] overflow-hidden rounded-4xl bg-stone/30 hover:shadow-soft-lg transition-all duration-500"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  loading={index < 2 ? 'eager' : 'lazy'}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-end p-4">
                  <div className="text-alabaster group-hover:text-white font-medium text-sm transition-colors">
                    {img.alt}
                  </div>
                </div>
                <div className="absolute top-4 left-4 text-4xl font-serif font-light text-alabaster/30">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="absolute inset-0 border border-stone/50 rounded-4xl" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="relative py-32 px-6 bg-alabaster border-t border-stone">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <div className="text-xs font-mono uppercase tracking-widest text-sand mb-4">
              Get Back on Track
            </div>
            <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-6 text-charcoal">
              Where would you
              <br />
              <span className="text-charcoal/60">like to go?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {quickLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                className="group relative p-8 bg-white border border-stone rounded-4xl shadow-soft hover:shadow-soft-lg transition-all duration-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="text-6xl md:text-8xl font-serif font-light text-olive/30">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <ArrowRight className="w-6 h-6 text-sand group-hover:text-terracotta group-hover:translate-x-1 transition-all duration-300" />
                </div>
                <h3 className="text-2xl md:text-3xl font-serif font-light mb-2 text-charcoal group-hover:text-olive transition-colors duration-300">
                  {link.label}
                </h3>
                <p className="text-sand font-light">
                  {link.description}
                </p>
              </Link>
            ))}
          </div>

          {/* Home Button */}
          <div className="mt-16 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sand hover:text-terracotta border-b border-sand/30 hover:border-terracotta/50 transition-all duration-300 font-light"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Immersive with Warm Overlay */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/styles-page/luxury_staging_style.webp"
            alt="Luxury staged room"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/60 to-olive/40" />
        </div>

        {/* Terracotta glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[60vh] bg-terracotta/10 blur-3xl rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="mb-8 inline-flex items-center px-4 py-2 rounded-full bg-alabaster/20 backdrop-blur-sm border border-alabaster/30">
            <span className="text-xs font-mono uppercase tracking-widest text-alabaster/90">
              Don't Let Empty Pages Stop You
            </span>
          </div>
          <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight mb-8 text-white">
            Start staging
            <br />
            <span className="text-alabaster/70">properties today</span>
          </h2>
          <p className="text-xl md:text-2xl text-alabaster/80 font-light max-w-2xl mx-auto mb-12 leading-relaxed">
            Get 3 free staged photos and see how AI-powered virtual staging can transform your listings.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center gap-2 px-12 py-4 bg-olive hover:bg-terracotta text-alabaster transition-all duration-500 text-lg font-medium rounded-full shadow-lift hover:-translate-y-0.5"
          >
            <Sparkles className="w-5 h-5" />
            Claim Your Free Credits
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
